import { isRemovedRateAlias, rateTypeDisplayLabel } from 'src/utils/rateTypeIdentity';
import {
  CompanyClientAgreementAssignmentData,
  CompanyClientAgreementTemplateData,
  CompanyClientAgreementVersionData,
  CompanyClientData,
  CompanyClientLocationData,
  CompanyWorkerContractTemplateData,
  RatePolicyBaseRate,
  RatePolicyConfig
} from 'src/contracts/companyProfile';

export interface QuoteRateTypeOption {
  value: string;
  label: string;
}

export interface ResolvedQuoteAgreementContext {
  assignment: CompanyClientAgreementAssignmentData | null;
  source: 'client-specific' | 'company-default' | 'none';
}

export interface QuoteAgreementOption {
  assignment: CompanyClientAgreementAssignmentData;
}

export const resolveQuoteClientRatePolicy = (
  localPolicy: { versionId: number; config: RatePolicyConfig } | null,
  agreementVersionId: number | undefined,
  fallbackPolicy: RatePolicyConfig | null
): RatePolicyConfig | null =>
  localPolicy && localPolicy.versionId === agreementVersionId
    ? localPolicy.config
    : fallbackPolicy;

export const formatClientLocationAddress = (
  location: CompanyClientLocationData
): string =>
  [
    location.address,
    location.city,
    location.state,
    location.postalCode,
    location.country
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

export const LEGACY_QUOTE_RATE_TYPES: QuoteRateTypeOption[] = [
  { value: 'TBD', label: 'TBD' },
  { value: 'DR-Full', label: 'DR-Full' },
  { value: 'Hourly', label: 'Hourly' },
  { value: 'Hourly-Full', label: 'Hourly-Full' },
  { value: 'Hourly-Half', label: 'Hourly-Half' },
  { value: 'Split Shift', label: 'Split Shift' },
  { value: '6 & Out', label: '6 & Out' }
];

// Custom identity is authored by the agreement builder; labels never identify a rule.
export const isCustomRate = (rate: Pick<RatePolicyBaseRate, 'code'>): boolean =>
  String(rate.code).startsWith('CUSTOM_');

export const isActiveCustomRate = (rate: Pick<RatePolicyBaseRate, 'code' | 'enabled'>): boolean =>
  isCustomRate(rate) && rate.enabled === true;

export const isEnabledAgreementRate = (rate: Pick<RatePolicyBaseRate, 'code' | 'enabled'>): boolean => rate.enabled === true && !isRemovedRateAlias(rate.code);

export const customRateSelectionWarning = (value: string, config?: RatePolicyConfig | null): string =>
  config?.baseRates.some(rate => isEnabledAgreementRate(rate) && rate.code === value)
    ? ''
    : 'Select an enabled rate from the bound Agreement, or continue with the entered values for review. The saved amounts are not policy-verified.';

const compatibleRateValue: Record<string, string> = {
  HOURLY: 'Hourly',
  FULL_DAY: 'DR-Full',
  SHOW_CALL: 'Show Call'
};

export const quoteRateTypeValue = (code: string): string =>
  compatibleRateValue[code] || code;

const clone = (config: RatePolicyConfig): RatePolicyConfig =>
  JSON.parse(JSON.stringify(config)) as RatePolicyConfig;

const withOverrides = (
  config: RatePolicyConfig,
  overrides: Record<string, unknown> | null
) => {
  const resolved = clone(config);
  const baseAmounts =
    overrides?.baseRateAmountsCents &&
    typeof overrides.baseRateAmountsCents === 'object' &&
    !Array.isArray(overrides.baseRateAmountsCents)
      ? (overrides.baseRateAmountsCents as Record<string, unknown>)
      : {};
  const premiumValues =
    overrides?.premiumAdjustmentValues &&
    typeof overrides.premiumAdjustmentValues === 'object' &&
    !Array.isArray(overrides.premiumAdjustmentValues)
      ? (overrides.premiumAdjustmentValues as Record<string, unknown>)
      : {};
  resolved.baseRates = (resolved.baseRates ?? []).map((rate) => {
    const fixedAmountCents = baseAmounts[rate.code];
    return typeof fixedAmountCents === 'number'
      ? { ...rate, amountSource: 'FIXED', fixedAmountCents }
      : rate;
  });
  resolved.premiums = (resolved.premiums ?? []).map((premium) => {
    const adjustmentValue = premiumValues[premium.code];
    return typeof adjustmentValue === 'number'
      ? { ...premium, adjustmentValue }
      : premium;
  });
  return resolved;
};

export const agreementQuotePolicyConfigs = (
  assignment: CompanyClientAgreementAssignmentData | null | undefined
): { worker: RatePolicyConfig; client: RatePolicyConfig } | null => {
  const binding = assignment?.version.rateBinding;
  if (!binding) return null;
  const workerConfig = binding.workerRatePolicyConfig
    ? clone(binding.workerRatePolicyConfig)
    : binding.workerRuleSetVersion?.config
    ? withOverrides(
        binding.workerRuleSetVersion.config,
        binding.workerRateOverrides
      )
    : null;
  const clientConfig = binding.clientRatePolicyConfig
    ? clone(binding.clientRatePolicyConfig)
    : binding.clientRuleSetVersion?.config
    ? withOverrides(
        binding.clientRuleSetVersion.config,
        binding.clientRateOverrides
      )
    : null;
  if (!workerConfig || !clientConfig) return null;
  return {
    worker: workerConfig,
    client: clientConfig
  };
};

export const composeQuotePolicyConfigs = (
  assignment: CompanyClientAgreementAssignmentData | null | undefined,
  workerContracts: CompanyWorkerContractTemplateData[] | null | undefined
): { worker: RatePolicyConfig; client: RatePolicyConfig } | null => {
  const agreementPolicies = agreementQuotePolicyConfigs(assignment);
  const activeWorkerContract = (workerContracts ?? []).find(
    (contract) => contract.status === 'ACTIVE'
  );
  const workerSnapshot =
    activeWorkerContract?.versions[0]?.workerRatePolicySnapshot;
  const draftPolicy = (assignment?.version as CompanyClientAgreementVersionData)?.clientRatePolicyDraft;
  if ((!agreementPolicies && !draftPolicy) || !workerSnapshot) return null;
  return {
    worker: clone(workerSnapshot),
    client: draftPolicy ? clone(draftPolicy) : agreementPolicies!.client
  };
};

export const agreementQuoteRateTypes = (
  assignment: CompanyClientAgreementAssignmentData | null | undefined
): QuoteRateTypeOption[] => {
  const binding = assignment?.version.rateBinding;
  const clientConfig = binding?.clientRatePolicyConfig
    ? clone(binding.clientRatePolicyConfig)
    : binding?.clientRuleSetVersion?.config
    ? withOverrides(
        binding.clientRuleSetVersion.config,
        binding.clientRateOverrides
      )
    : null;
  const baseRates = clientConfig?.baseRates ?? [];
  return baseRates
    .filter(isEnabledAgreementRate)
    .map((rate) => ({
      value: rate.code,
      label: rateTypeDisplayLabel(rate.code, rate.label)
    }));
};

export const quotePolicyRateTypes = (
  config: RatePolicyConfig | null | undefined
): QuoteRateTypeOption[] =>
  (config?.baseRates ?? [])
    .filter(isEnabledAgreementRate)
    .map((rate) => ({
      value: rate.code,
      label: rateTypeDisplayLabel(rate.code, rate.label)
    }));

const assignmentForVersion = (
  template: CompanyClientAgreementTemplateData,
  version: CompanyClientAgreementVersionData
): CompanyClientAgreementAssignmentData => ({
  id: 0,
  inherited: true,
  effectiveAt: version.effectiveAt ?? version.createdAt,
  template: {
    id: template.id,
    name: template.name,
    status: template.status,
    isDefault: template.isDefault
  },
  version
});

export const selectableQuoteAgreements = (
  client: CompanyClientData | null | undefined,
  templates: CompanyClientAgreementTemplateData[] | null | undefined,
  assigned: CompanyClientAgreementAssignmentData | null | undefined
): QuoteAgreementOption[] => {
  if (!client) return [];
  const seen = new Set<number>();
  const options: QuoteAgreementOption[] = [];
  const add = (assignment: CompanyClientAgreementAssignmentData) => {
    if (
      assignment.template.status === 'ARCHIVED' ||
      Boolean(assignment.version.retiredAt) ||
      (assignment.version.clientId != null && assignment.version.clientId !== client.id)
    ) return;
    if (seen.has(assignment.version.id)) return;
    seen.add(assignment.version.id);
    options.push({ assignment });
  };

  if (assigned?.version.rateBinding) add(assigned);
  (templates ?? [])
    .filter((template) => template.status === 'ACTIVE' && (!client.companyId || !template.companyId || template.companyId === client.companyId))
    .sort(
      (left, right) =>
        Number(right.isDefault) - Number(left.isDefault) ||
        left.name.localeCompare(right.name)
    )
    .forEach((template) => {
      [...template.versions]
        .filter(
          (version) =>
            Boolean(version.rateBinding) &&
            !version.retiredAt &&
            (version.status === 'PUBLISHED' || version.status === undefined) &&
            (version.clientId === null || version.clientId === client.id)
        )
        .sort((left, right) => right.version - left.version)
        .forEach((version) => add(assignmentForVersion(template, version)));
    });
  return options;
};

export const quoteRateTypeCatalog = (
  configs: Array<RatePolicyConfig | null | undefined>
): RatePolicyBaseRate[] => {
  const seen = new Set<string>();
  return configs.flatMap((config) =>
    (config?.baseRates ?? []).flatMap((rate) => {
      if (!isCustomRate(rate) || seen.has(rate.code)) return [];
      seen.add(rate.code);
      return [{ ...rate }];
    })
  );
};

export const setQuoteRateTypeEnabled = (
  config: RatePolicyConfig,
  rate: RatePolicyBaseRate,
  enabled: boolean
): RatePolicyConfig => {
  if (!isCustomRate(rate)) return config;
  if (
    !enabled &&
    config.baseRates.filter((candidate) => candidate.enabled).length <= 1
  ) {
    return config;
  }
  const existing = config.baseRates.some(
    (candidate) => candidate.code === rate.code
  );
  return {
    ...config,
    baseRates: existing
      ? config.baseRates.map((candidate) =>
          candidate.code === rate.code ? { ...candidate, enabled } : candidate
        )
      : [...config.baseRates, { ...rate, enabled }]
  };
};

export const quoteAgreementPolicyOverride = (
  source: { worker: RatePolicyConfig; client: RatePolicyConfig } | null,
  client: RatePolicyConfig | null | undefined
) => {
  if (
    !source ||
    !client ||
    JSON.stringify(source.client) === JSON.stringify(client)
  ) {
    return undefined;
  }
  return {
    workerRatePolicySnapshot: source.worker,
    clientRatePolicySnapshot: client
  };
};

export const resolveQuoteAgreementContext = (
  client: CompanyClientData | null | undefined,
  templates: CompanyClientAgreementTemplateData[] | null | undefined
): ResolvedQuoteAgreementContext => {
  if (!client) {
    return { assignment: null, source: 'none' };
  }

  if (client?.agreementAssignment?.version.rateBinding) {
    return {
      assignment: client.agreementAssignment,
      source: 'client-specific'
    };
  }

  const template = (templates ?? []).find(
    (candidate) => candidate.isDefault && candidate.status === 'ACTIVE'
  );
  const version = template?.versions.find(
    (candidate) =>
      candidate.clientId === null &&
      candidate.rateBinding &&
      (candidate.status === 'PUBLISHED' || candidate.status === undefined)
  );
  if (!template || !version) {
    return { assignment: null, source: 'none' };
  }

  return {
    assignment: {
      id: 0,
      inherited: true,
      effectiveAt: version.effectiveAt ?? version.createdAt,
      template: {
        id: template.id,
        name: template.name,
        status: template.status,
        isDefault: true
      },
      version
    },
    source: 'company-default'
  };
};
